import type { Ship, ShipType } from '@/types';

// ---------------------------------------------------------------------------
// Live AIS — aisstream.io WebSocket
//
// 무료 API 키 필요: https://aisstream.io  →  .env 의 VITE_AISSTREAM_KEY
// 키가 없으면 이 모듈은 아무것도 하지 않고, ship-tracker 의 시뮬레이션이 유지된다.
//
// 왜 WebSocket 을 브라우저에서 직접 여는가:
//   Vercel 서버리스는 장기 연결을 유지할 수 없다(요청-응답 모델). AIS 는
//   스트리밍이므로 클라이언트가 직접 붙는 게 유일하게 동작하는 구조다.
//   → 키는 클라이언트에 노출된다. 무료 키 전용으로만 쓸 것.
// ---------------------------------------------------------------------------

const API_KEY = import.meta.env.VITE_AISSTREAM_KEY as string | undefined;
export const HAS_AIS = Boolean(API_KEY);

const ENDPOINT = 'wss://stream.aisstream.io/v0/stream';

/** 전 세계를 구독하면 초당 수천 건이라 브라우저가 못 버틴다 → 주요 해역만. */
const BOXES: number[][][] = [
  [[18, 105], [45, 145]],    // 동아시아 — 한중일, 동중국해
  [[-10, 95], [20, 125]],    // 동남아 — 말라카, 남중국해
  [[10, 32], [32, 60]],      // 홍해 · 호르무즈 · 아라비아해
  [[30, -10], [46, 37]],     // 지중해
  [[48, -12], [62, 30]],     // 북해 · 발트해
  [[24, -85], [46, -60]],    // 북미 동안
  [[30, -130], [50, -115]],  // 북미 서안
];

const MAX_SHIPS = 4000;
const FLUSH_MS = 2000;
/** 이 시간 동안 갱신이 없으면 목록에서 제거 */
const STALE_MS = 12 * 60 * 1000;

// ── MMSI 앞 3자리(MID) → 선적국 ────────────────────────────
const MID: Record<string, [string, string]> = {
  '440': ['South Korea', 'KR'], '441': ['South Korea', 'KR'],
  '431': ['Japan', 'JP'], '432': ['Japan', 'JP'],
  '412': ['China', 'CN'], '413': ['China', 'CN'], '414': ['China', 'CN'],
  '477': ['Hong Kong', 'HK'], '416': ['Taiwan', 'TW'],
  '563': ['Singapore', 'SG'], '564': ['Singapore', 'SG'], '565': ['Singapore', 'SG'], '566': ['Singapore', 'SG'],
  '538': ['Marshall Islands', 'MH'], '636': ['Liberia', 'LR'], '637': ['Liberia', 'LR'],
  '352': ['Panama', 'PA'], '353': ['Panama', 'PA'], '354': ['Panama', 'PA'], '370': ['Panama', 'PA'],
  '371': ['Panama', 'PA'], '372': ['Panama', 'PA'], '373': ['Panama', 'PA'],
  '249': ['Malta', 'MT'], '248': ['Malta', 'MT'], '256': ['Malta', 'MT'],
  '209': ['Cyprus', 'CY'], '210': ['Cyprus', 'CY'], '212': ['Cyprus', 'CY'],
  '211': ['Germany', 'DE'], '218': ['Germany', 'DE'],
  '226': ['France', 'FR'], '227': ['France', 'FR'], '228': ['France', 'FR'],
  '232': ['United Kingdom', 'GB'], '233': ['United Kingdom', 'GB'],
  '234': ['United Kingdom', 'GB'], '235': ['United Kingdom', 'GB'],
  '244': ['Netherlands', 'NL'], '245': ['Netherlands', 'NL'], '246': ['Netherlands', 'NL'],
  '247': ['Italy', 'IT'], '224': ['Spain', 'ES'], '225': ['Spain', 'ES'],
  '257': ['Norway', 'NO'], '258': ['Norway', 'NO'], '259': ['Norway', 'NO'],
  '265': ['Sweden', 'SE'], '266': ['Sweden', 'SE'],
  '219': ['Denmark', 'DK'], '220': ['Denmark', 'DK'], '230': ['Finland', 'FI'],
  '273': ['Russia', 'RU'], '271': ['Turkey', 'TR'], '272': ['Ukraine', 'UA'],
  '338': ['United States', 'US'], '366': ['United States', 'US'], '367': ['United States', 'US'],
  '368': ['United States', 'US'], '369': ['United States', 'US'],
  '316': ['Canada', 'CA'], '503': ['Australia', 'AU'], '512': ['New Zealand', 'NZ'],
  '419': ['India', 'IN'], '422': ['Iran', 'IR'], '470': ['UAE', 'AE'], '471': ['UAE', 'AE'],
  '403': ['Saudi Arabia', 'SA'], '525': ['Indonesia', 'ID'], '533': ['Malaysia', 'MY'],
  '567': ['Thailand', 'TH'], '574': ['Vietnam', 'VN'], '548': ['Philippines', 'PH'],
  '621': ['Egypt', 'EG'], '605': ['Algeria', 'DZ'], '710': ['Brazil', 'BR'],
};

function flagFromMmsi(mmsi: number): { flag: string; flagCode: string } {
  const hit = MID[String(mmsi).slice(0, 3)];
  return hit ? { flag: hit[0], flagCode: hit[1] } : { flag: 'Unknown', flagCode: '' };
}

/** AIS ShipType 코드(0-99) → 우리 ShipType */
function shipTypeFromAis(code?: number): ShipType {
  if (code == null) return 'other';
  if (code >= 80 && code <= 89) return 'tanker';
  if (code >= 70 && code <= 79) return 'cargo';
  if (code >= 60 && code <= 69) return 'passenger';
  return 'other';
}

// ── 수신 메시지 타입 (필요한 필드만) ────────────────────────
interface AisEnvelope {
  MessageType?: string;
  MetaData?: { MMSI?: number; ShipName?: string; latitude?: number; longitude?: number };
  Message?: {
    PositionReport?: { Latitude?: number; Longitude?: number; Sog?: number; Cog?: number; TrueHeading?: number };
    ShipStaticData?: { Name?: string; Destination?: string; CallSign?: string; Type?: number };
  };
}

// ── 스트림 상태 ────────────────────────────────────────────
const vessels = new Map<number, Ship>();
let socket: WebSocket | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let retry = 0;
let stopped = false;
let onUpdate: ((ships: Ship[]) => void) | null = null;

function flush() {
  const cutoff = Date.now() - STALE_MS;
  for (const [mmsi, s] of vessels) {
    if ((s.lastSeen ?? 0) < cutoff) vessels.delete(mmsi);
  }
  // 상한 초과 시 오래된 것부터 버린다
  if (vessels.size > MAX_SHIPS) {
    const sorted = [...vessels.entries()].sort((a, b) => (a[1].lastSeen ?? 0) - (b[1].lastSeen ?? 0));
    for (let i = 0; i < sorted.length - MAX_SHIPS; i++) vessels.delete(sorted[i][0]);
  }
  onUpdate?.([...vessels.values()]);
}

function handle(env: AisEnvelope) {
  const mmsi = env.MetaData?.MMSI;
  if (!mmsi) return;

  const pos = env.Message?.PositionReport;
  const stat = env.Message?.ShipStaticData;
  const lat = pos?.Latitude ?? env.MetaData?.latitude;
  const lon = pos?.Longitude ?? env.MetaData?.longitude;

  const prev = vessels.get(mmsi);
  if (lat == null || lon == null) {
    // 정적 정보만 온 경우 — 기존 레코드를 보강한다
    if (prev && stat) {
      vessels.set(mmsi, {
        ...prev,
        name: stat.Name?.trim() || prev.name,
        destination: stat.Destination?.trim() || prev.destination,
        callSign: stat.CallSign?.trim() || prev.callSign,
        type: stat.Type != null ? shipTypeFromAis(stat.Type) : prev.type,
      });
    }
    return;
  }

  const { flag, flagCode } = flagFromMmsi(mmsi);
  // TrueHeading 511 = 미상 → COG 로 대체
  const hdg = pos?.TrueHeading != null && pos.TrueHeading < 360 ? pos.TrueHeading : (pos?.Cog ?? prev?.heading ?? 0);

  vessels.set(mmsi, {
    id: `ais-${mmsi}`,
    mmsi,
    name: stat?.Name?.trim() || env.MetaData?.ShipName?.trim() || prev?.name || `MMSI ${mmsi}`,
    type: stat?.Type != null ? shipTypeFromAis(stat.Type) : (prev?.type ?? 'other'),
    lat, lon,
    heading: hdg,
    speed: pos?.Sog ?? prev?.speed ?? 0,
    flag, flagCode,
    isLive: true,
    callSign: stat?.CallSign?.trim() || prev?.callSign,
    destination: stat?.Destination?.trim() || prev?.destination,
    lastSeen: Date.now(),
  });
}

function connect() {
  if (stopped || !API_KEY) return;

  socket = new WebSocket(ENDPOINT);

  socket.onopen = () => {
    retry = 0;
    socket?.send(JSON.stringify({
      APIKey: API_KEY,
      BoundingBoxes: BOXES,
      FilterMessageTypes: ['PositionReport', 'ShipStaticData'],
    }));
    console.log('[AIS] connected — subscribed to', BOXES.length, 'regions');
  };

  socket.onmessage = (ev) => {
    try { handle(JSON.parse(ev.data as string) as AisEnvelope); } catch { /* 잘린 프레임 무시 */ }
  };

  socket.onerror = () => { /* onclose 가 이어서 처리 */ };

  socket.onclose = () => {
    socket = null;
    if (stopped) return;
    // 지수 백오프 (최대 30초)
    const delay = Math.min(30_000, 1000 * 2 ** retry++);
    console.warn(`[AIS] disconnected — retrying in ${delay / 1000}s`);
    setTimeout(connect, delay);
  };
}

/** AIS 스트림 시작. 키가 없으면 false 를 반환하고 아무것도 하지 않는다. */
export function startAisStream(cb: (ships: Ship[]) => void): boolean {
  if (!API_KEY) return false;
  if (socket) return true;
  stopped = false;
  onUpdate = cb;
  connect();
  flushTimer = setInterval(flush, FLUSH_MS);
  return true;
}

export function stopAisStream(): void {
  stopped = true;
  onUpdate = null;
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
  socket?.close();
  socket = null;
  vessels.clear();
}
